import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Clock, Users, TrendingUp, Globe, Mail, Phone, ChevronLeft, ChevronRight, ArrowRight, X, ExternalLink, Facebook, Instagram } from 'lucide-react';
import { Dialog, DialogContent } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Floating Dots Background Component
const FloatingDots = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let dots = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createDots = () => {
      dots = [];
      const numDots = 60;
      for (let i = 0; i < numDots; i++) {
        dots.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2.5 + 1,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          opacity: Math.random() * 0.4 + 0.15,
          fadeSpeed: (Math.random() - 0.5) * 0.008
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      dots.forEach(dot => {
        dot.x += dot.vx;
        dot.y += dot.vy;
        dot.opacity += dot.fadeSpeed;
        
        if (dot.opacity <= 0.15 || dot.opacity >= 0.7) {
          dot.fadeSpeed = -dot.fadeSpeed;
        }
        
        if (dot.x < 0) dot.x = canvas.width;
        if (dot.x > canvas.width) dot.x = 0;
        if (dot.y < 0) dot.y = canvas.height;
        if (dot.y > canvas.height) dot.y = 0;
        
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 168, 83, ${dot.opacity})`;
        ctx.fill();
      });
      
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createDots();
    animate();

    const handleResize = () => {
      resizeCanvas();
      createDots();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 15 }}
    />
  );
};

// Scroll Animation Hook
const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// DITP Logo Component - using uploaded image
const DITPLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_thai-brands-admin/artifacts/7a0r49zf_ditp-logo.png" 
    alt="DITP Singapore" 
    className="h-8 w-auto"
  />
);

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { id: 'about', label: 'ABOUT' },
    { id: 'why-attend', label: 'WHY ATTEND' },
    { id: 'brands', label: 'BRANDS' },
    { id: 'schedule', label: 'SCHEDULE' },
    { id: 'gallery', label: 'GALLERY' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || mobileMenuOpen ? 'bg-[#0A3D62]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`} data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex flex-col" data-testid="logo-link">
            <span className="font-heading text-xl font-bold text-white tracking-wide">THAI FRANCHISE</span>
            <span className="text-xs text-accent tracking-widest font-heading">GLOBAL MOVE 2026, SINGAPORE</span>
          </Link>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium tracking-wider transition-all duration-300 relative py-2 ${
                  activeSection === item.id 
                    ? 'text-accent' 
                    : 'text-white hover:text-accent'
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-accent transform transition-transform duration-300 ${
                  activeSection === item.id ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </button>
            ))}
          </div>
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5"
            data-testid="mobile-menu-toggle"
          >
            <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>
      </div>
      {/* Mobile menu dropdown */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-80 border-t border-accent/20' : 'max-h-0'}`}>
        <div className="px-6 py-4 space-y-1 bg-[#0A3D62]/95 backdrop-blur-md">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`block w-full text-left py-3 px-4 rounded-lg text-sm font-medium tracking-wider transition-colors ${
                activeSection === item.id
                  ? 'text-accent bg-accent/10'
                  : 'text-white/80 hover:text-accent hover:bg-accent/5'
              }`}
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-[#0A3D62]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0d4a75] via-[#0A3D62] to-[#072942] opacity-80"></div>
    </div>
    <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-20 sm:pt-0">
      <div className="mb-4 animate-fadeInUp">
        <h1 className="font-heading text-5xl md:text-7xl font-bold text-white tracking-wide" style={{ letterSpacing: '0.05em' }} data-testid="hero-title">
          THAI FRANCHISE
        </h1>
      </div>
      <div className="mb-4 animate-fadeInUp animation-delay-100">
        <h2 className="font-heading text-3xl md:text-5xl font-bold text-accent tracking-wide" style={{ letterSpacing: '0.12em' }} data-testid="hero-subtitle">
          GLOBAL MOVE 2026
        </h2>
      </div>
      <div className="flex items-center justify-center gap-4 mb-12 animate-fadeInUp animation-delay-200">
        <div className="flex-1 h-px bg-white/40 max-w-[120px] md:max-w-[180px]"></div>
        <span className="text-lg md:text-xl text-white/80 tracking-[0.3em] whitespace-nowrap">SINGAPORE</span>
        <div className="flex-1 h-px bg-white/40 max-w-[120px] md:max-w-[180px]"></div>
      </div>
      <div className="flex flex-wrap justify-center gap-4 mb-12 animate-fadeInUp animation-delay-300">
        <div className="glass px-6 py-3 rounded-full flex items-center gap-2 border-2 border-accent/30">
          <Calendar className="w-5 h-5 text-accent" />
          <span className="text-white font-medium">25 MARCH 2026</span>
        </div>
        <div className="glass px-6 py-3 rounded-full flex items-center gap-2 border-2 border-accent/30">
          <MapPin className="w-5 h-5 text-accent" />
          <span className="text-white font-medium">ROYAL THAI EMBASSY SINGAPORE</span>
        </div>
      </div>
      <a
        href="#gallery"
        className="btn-gold inline-flex items-center gap-2 animate-fadeInUp animation-delay-400"
        data-testid="hero-gallery-btn"
      >
        VIEW GALLERY <ArrowRight className="w-4 h-4" />
      </a>
      <div className="mt-20 animate-fadeInUp animation-delay-500">
        <p className="text-white/60 text-sm tracking-wider mb-4">ORGANIZED BY</p>
        <div className="flex justify-center">
          <DITPLogo />
        </div>
      </div>
    </div>
  </section>
);

// About Section
const AboutSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);
  
  return (
    <section id="about" className="py-20 md:py-32 bg-[#0A3D62] relative" data-testid="about-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <p className="text-accent text-sm tracking-[0.3em] mb-4">ABOUT THE EVENT</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-2">BRIDGE TO</h2>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-accent mb-8">SUCCESS</h2>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              Presented by the Thai Trade Center Singapore under Department of International Trade and Promotion (DITP), Thailand's Ministry of Commerce, this exclusive event connects Thailand's most promising franchise brands with Singapore's vibrant investment community.
            </p>
            <p className="text-white/80 text-lg leading-relaxed mb-10">
              Discover unparalleled opportunities to expand your business portfolio across ASEAN markets. Meet 19 curated Thai franchise brands and explore investment opportunities worth over $2 million.
            </p>
            <div className="grid grid-cols-3 gap-8 mb-10">
              <div className={`text-center transition-all duration-500 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <Users className="w-8 h-8 text-accent mx-auto mb-2" />
                <p className="text-3xl font-bold text-accent">19</p>
                <p className="text-white/60 text-sm">Thai Brands</p>
              </div>
              <div className={`text-center transition-all duration-500 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <TrendingUp className="w-8 h-8 text-accent mx-auto mb-2" />
                <p className="text-3xl font-bold text-accent">$2M+</p>
                <p className="text-white/60 text-sm">Investment Opportunities</p>
              </div>
              <div className={`text-center transition-all duration-500 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <Calendar className="w-8 h-8 text-accent mx-auto mb-2" />
                <p className="text-3xl font-bold text-accent">1</p>
                <p className="text-white/60 text-sm">Day Event</p>
              </div>
            </div>
            <a href="#gallery" className="btn-gold inline-flex items-center gap-2" data-testid="about-gallery-btn">
              VIEW GALLERY <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className={`relative transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="glass rounded-xl p-8 relative overflow-hidden border-2 border-accent/30">
              <div className="absolute top-4 right-4 bg-accent text-[#0A3D62] px-4 py-2 rounded-full text-xs font-bold">
                VIP ACCESS
              </div>
              <h3 className="font-heading text-3xl font-bold text-white mb-2">25 MARCH</h3>
              <p className="text-accent text-lg mb-8">2026</p>
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2"></div>
                  <div>
                    <p className="text-accent font-semibold">13:00 - 13:30 hrs - Registration</p>
                    <p className="text-white/60 text-sm">Check-in and welcome</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2"></div>
                  <div>
                    <p className="text-accent font-semibold">13:30 - 17:00 hrs - Presentations</p>
                    <p className="text-white/60 text-sm">Opening Ceremony, franchise presentations & networking</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2"></div>
                  <div>
                    <p className="text-accent font-semibold">17:00 hrs - End of Program</p>
                    <p className="text-white/60 text-sm">Closing of the event</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-[#1a5276]">
                <p className="text-accent text-sm mb-1">Venue: <span className="text-white font-semibold">Royal Thai Embassy Singapore</span></p>
                <p className="text-white/60 text-sm">370 Orchard Road, Singapore 238870</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Attend Section
const WhyAttendSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);
  
  const reasons = [
    {
      number: '01',
      icon: Users,
      title: 'NETWORK',
      description: 'Connect with C-level executives, decision makers, and industry leaders from Thailand and Singapore. Build relationships that drive business growth.'
    },
    {
      number: '02',
      icon: TrendingUp,
      title: 'INVEST',
      description: 'Explore vetted, high-ROI franchise opportunities across F&B, retail, education and other industries. Access exclusive investment deals.'
    },
    {
      number: '03',
      icon: Globe,
      title: 'EXPAND',
      description: 'Scale your brand into the lucrative ASEAN market. Leverage Singapore as your gateway to regional expansion with expert guidance.'
    }
  ];

  return (
    <section id="why-attend" className="py-20 md:py-32 bg-[#0A3D62]" data-testid="why-attend-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-accent text-sm tracking-[0.3em] mb-4">OPPORTUNITIES</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold">
            <span className="text-white">WHY </span>
            <span className="text-accent">ATTEND</span>
          </h2>
          <p className="text-white/60 mt-4 max-w-2xl mx-auto">
            Three transformative steps designed to accelerate your business success in the ASEAN franchise market
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <div
              key={reason.number}
              className={`card-dark rounded-xl p-8 relative group border-2 border-[#1a5276] hover:border-accent/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              data-testid={`why-attend-card-${index}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 rounded-lg bg-[#072942] border-2 border-accent/30 flex items-center justify-center">
                  <reason.icon className="w-6 h-6 text-accent" />
                </div>
                <span className="text-4xl font-bold text-accent/30 group-hover:text-accent/50 transition-colors">{reason.number}</span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">{reason.title}</h3>
              <p className="text-white/60 leading-relaxed">{reason.description}</p>
            </div>
          ))}
        </div>
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
          <p className="text-white/60 mb-6">Limited spots available for qualified investors and franchisees</p>
          <a href="#gallery" className="btn-gold-filled inline-flex items-center gap-2" data-testid="why-attend-gallery-btn">
            VIEW GALLERY <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Featured Brands Section
const FeaturedBrandsSection = ({ brands, onBrandClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const featuredBrands = brands.filter(b => b.is_featured);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredBrands.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredBrands.length) % featuredBrands.length);
  };

  if (featuredBrands.length === 0) {
    return (
      <section id="brands" className="py-20 md:py-32 bg-[#0A3D62]" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-accent text-sm tracking-[0.3em] mb-4">CURATED SELECTION</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">MEET THE </span>
            <span className="text-accent">BRANDS</span>
          </h2>
          <p className="text-white/60 mb-12">Discover Thailand's finest franchise opportunities, vetted for Singapore market success</p>
          <p className="text-white/40">Featured brands coming soon...</p>
          <Link to="/brands" className="text-accent hover:underline inline-flex items-center gap-2 mt-8" data-testid="view-all-brands-link">
            View All Brands <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    );
  }

  const currentBrand = featuredBrands[currentIndex];

  return (
    <section id="brands" className="py-20 md:py-32 bg-[#0A3D62]" data-testid="brands-section">
      <div className="absolute left-0 right-0 h-px bg-accent/20"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-accent text-sm tracking-[0.3em] mb-4">CURATED SELECTION</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">MEET THE </span>
            <span className="text-accent">BRANDS</span>
          </h2>
          <p className="text-white/60">Discover Thailand's finest franchise opportunities, vetted for Singapore market success</p>
        </div>
        <div className="relative flex items-center justify-center">
          <button
            onClick={prevSlide}
            className="absolute left-0 z-10 w-12 h-12 rounded-full border border-accent/50 flex items-center justify-center text-accent hover:bg-accent hover:text-[#0A3D62] transition-colors"
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="max-w-xl mx-auto">
            <div
              className="card-dark rounded-xl p-8 text-center cursor-pointer border-2 border-[#1a5276] hover:border-accent/50 transition-colors"
              onClick={() => onBrandClick(currentBrand)}
              data-testid={`brand-card-${currentBrand.id}`}
            >
              <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-[#072942] border-2 border-accent/30 flex items-center justify-center overflow-hidden">
                {currentBrand.logo ? (
                  <img src={currentBrand.logo} alt={currentBrand.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-accent">{currentBrand.name.charAt(0)}</span>
                )}
              </div>
              <span className="badge-category mb-4 inline-block">{currentBrand.category}</span>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">{currentBrand.name}</h3>
              <p className="text-white/60 leading-relaxed">{currentBrand.short_description}</p>
            </div>
          </div>
          <button
            onClick={nextSlide}
            className="absolute right-0 z-10 w-12 h-12 rounded-full border border-accent/50 flex items-center justify-center text-accent hover:bg-accent hover:text-[#0A3D62] transition-colors"
            data-testid="carousel-next-btn"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
        <div className="flex justify-center gap-2 mt-8">
          {featuredBrands.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/brands" className="text-accent hover:underline inline-flex items-center gap-2" data-testid="view-all-brands-link">
            View All Brands <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Schedule Section
const ScheduleSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);
  
  const schedule = [
    { time: '13:00 - 13:30', title: 'Registration', description: 'Check-in and welcome at the Royal Thai Embassy', icon: 'clipboard' },
    { time: '13:30 - 13:45', title: 'Opening Ceremony', description: 'Welcome address by Director, Thai Trade Center, Singapore', icon: 'mic' },
    { time: '13:45 - 14:10', title: 'Presentation of Thai Franchise Brands', description: 'Showcase of 19 leading Thai franchise brands across F&B, retail, and service sectors', icon: 'presentation' },
    { time: '14:10 - 17:00', title: 'Networking and Business Matching Session', description: 'One-on-one discussions between investors and brand representatives', icon: 'users' },
    { time: '17:00', title: 'End of Program', description: 'Closing of Thai Franchise Global Move 2026, Singapore', icon: 'check' }
  ];

  // Gold line art icons
  const getIcon = (type) => {
    const iconClass = "w-6 h-6 text-accent stroke-[1.5]";
    switch(type) {
      case 'clipboard':
        return (
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
            <rect x="9" y="3" width="6" height="4" rx="1"/>
            <path d="M9 12h6M9 16h6"/>
          </svg>
        );
      case 'mic':
        return (
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="22"/>
          </svg>
        );
      case 'presentation':
        return (
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="2" y="3" width="20" height="14" rx="2"/>
            <path d="M8 21h8M12 17v4"/>
            <path d="M7 8l3 3 4-4 3 3"/>
          </svg>
        );
      case 'users':
        return (
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        );
      case 'check':
        return (
          <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        );
      default:
        return <Clock className={iconClass} />;
    }
  };

  return (
    <section id="schedule" className="py-20 md:py-32 bg-[#0A3D62]" data-testid="schedule-section" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-accent text-sm tracking-[0.3em] mb-4">PROGRAM</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
            <span className="text-white">EVENT </span>
            <span className="text-accent">SCHEDULE</span>
          </h2>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-4 text-white/60">
            <span className="flex items-center justify-center gap-2"><Calendar className="w-4 h-4 text-accent" /> <span className="text-accent">25 March 2026</span></span>
            <span className="text-accent hidden sm:block">|</span>
            <span className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4 text-accent" /> Royal Thai Embassy Singapore</span>
            <span className="text-accent hidden sm:block">|</span>
            <span className="flex items-center justify-center gap-2"><Clock className="w-4 h-4 text-accent" /> <span className="text-accent">13:00 - 17:00 hrs</span></span>
          </div>
        </div>
        <div className="space-y-6">
          {schedule.map((item, index) => (
            <div
              key={index}
              className={`card-dark rounded-xl p-4 sm:p-6 flex flex-col sm:flex-row items-start gap-3 sm:gap-6 border-2 border-[#1a5276] hover:border-accent/50 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
              data-testid={`schedule-item-${index}`}
            >
              <div className="flex items-center gap-3 sm:gap-4 sm:min-w-[140px]">
                <Clock className="w-5 h-5 text-accent flex-shrink-0" />
                <span className="text-accent font-bold text-base sm:text-lg">{item.time}</span>
              </div>
              <div className="flex items-start gap-3 sm:gap-4 w-full">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#072942] border-2 border-accent/30 flex items-center justify-center flex-shrink-0">
                  {getIcon(item.icon)}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading text-base sm:text-xl font-semibold text-accent mb-1">{item.title}</h4>
                  <p className="text-white/60 text-sm sm:text-base">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={`mt-12 card-dark rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-2 border-[#1a5276] transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-start gap-4">
            <MapPin className="w-6 h-6 text-accent mt-1" />
            <div>
              <h4 className="font-heading text-xl font-semibold text-white">Royal Thai Embassy Singapore</h4>
              <p className="text-white/60">370 Orchard Road, Singapore 238870</p>
              <p className="text-accent text-sm mt-1">Nearest MRT: Orchard Station (NS22)</p>
            </div>
          </div>
          <a
            href="https://maps.google.com/?q=Royal+Thai+Embassy+Singapore"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold inline-flex items-center gap-2"
            data-testid="get-directions-btn"
          >
            GET DIRECTIONS <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Gallery Carousel Section
const GalleryCarouselSection = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryImages(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
    };
    fetchGallery();
  }, []);

  useEffect(() => {
    if (galleryImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [galleryImages]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const openLightbox = (url) => {
    setLightboxImage(url);
    setLightboxOpen(true);
  };

  return (
    <>
      <section id="gallery" ref={sectionRef} className="py-20 md:py-32 bg-[#0A3D62]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-accent text-sm tracking-[0.3em] mb-4">EVENT HIGHLIGHTS</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              <span className="text-white">PHOTO </span>
              <span className="text-accent">GALLERY</span>
            </h2>
            <p className="text-white/60">Explore moments from Thai Franchise Global Move events</p>
          </div>

          {galleryImages.length > 0 ? (
            <>
              {/* Main Carousel */}
              <div className={`relative overflow-hidden rounded-xl border-2 border-[#1a5276] mb-8 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-black/30">
                  {galleryImages.map((img, index) => (
                    <img
                      key={img.id}
                      src={img.image_url}
                      alt={img.caption || `Gallery ${index + 1}`}
                      className={`absolute inset-0 w-full h-full object-contain cursor-pointer transition-opacity duration-700 ${index === currentIndex ? 'opacity-100' : 'opacity-0'}`}
                      onClick={() => openLightbox(img.image_url)}
                    />
                  ))}
                  {galleryImages[currentIndex]?.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 sm:p-6">
                      <p className="text-white text-sm sm:text-base">{galleryImages[currentIndex].caption}</p>
                    </div>
                  )}
                </div>
                {/* Dots indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {galleryImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${index === currentIndex ? 'bg-accent w-6' : 'bg-white/40 hover:bg-white/60'}`}
                      data-testid={`gallery-dot-${index}`}
                    />
                  ))}
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className={`flex gap-3 overflow-x-auto pb-2 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                {galleryImages.map((img, index) => (
                  <img
                    key={img.id}
                    src={img.image_url}
                    alt={img.caption || `Thumbnail ${index + 1}`}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover flex-shrink-0 cursor-pointer transition-all border-2 ${index === currentIndex ? 'border-accent opacity-100' : 'border-transparent opacity-60 hover:opacity-80'}`}
                  />
                ))}
              </div>

              {/* View All Link */}
              <div className="text-center mt-8">
                <Link to="/gallery" className="btn-gold inline-flex items-center gap-2" data-testid="view-all-gallery-btn">
                  VIEW ALL PHOTOS <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-16 card-dark rounded-xl border-2 border-[#1a5276]">
              <p className="text-white/40">Gallery images coming soon</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent hideCloseButton className="max-w-5xl bg-black/95 border-none p-2" data-testid="gallery-lightbox">
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightboxImage}
            alt="Gallery full view"
            className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Footer Section
const Footer = () => (
  <footer className="py-16 bg-[#072942] border-t border-accent/30" data-testid="footer-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid md:grid-cols-3 gap-12 mb-12">
        <div>
          <div className="mb-6">
            <span className="font-heading text-xl font-bold text-white">THAI FRANCHISE</span>
            <p className="text-accent text-xs tracking-widest font-heading">GLOBAL MOVE 2026, SINGAPORE</p>
          </div>
          <p className="text-white/60 text-sm mb-6">
            The premier platform connecting Thailand's finest franchise brands with Singapore's investment community.
          </p>
          <div className="flex gap-3">
            <a href="https://www.facebook.com/thaitradecenter.singapore" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#0A3D62] border-2 border-accent/30 flex items-center justify-center text-accent hover:bg-accent hover:text-[#0A3D62] transition-colors">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://www.instagram.com/thaitrade_sg/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#0A3D62] border-2 border-accent/30 flex items-center justify-center text-accent hover:bg-accent hover:text-[#0A3D62] transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>
        <div>
          <h4 className="font-heading text-lg font-semibold text-accent mb-6">QUICK LINKS</h4>
          <ul className="space-y-3">
            {['About', 'Why Attend', 'Brands', 'Schedule', 'Gallery'].map((item) => (
              <li key={item}>
                <a href={`#${item.toLowerCase().replace(' ', '-')}`} className="text-white/60 hover:text-accent transition-colors">
                  {item}
                </a>
              </li>
            ))}
            <li>
              <Link to="/admin/login" className="text-white/60 hover:text-accent transition-colors" data-testid="footer-admin-link">
                Admin
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading text-lg font-semibold text-accent mb-6">CONTACT US</h4>
          <div className="space-y-4">
            <div>
              <p className="text-white/40 text-sm">Email</p>
              <a href="mailto:enquiry@thaitrade.sg" className="text-accent hover:underline">enquiry@thaitrade.sg</a>
            </div>
            <div>
              <p className="text-white/40 text-sm">Phone</p>
              <a href="tel:+6567373060" className="text-accent hover:underline">+65 6737 3060</a>
            </div>
            <div>
              <p className="text-white/40 text-sm">Venue</p>
              <p className="text-white flex items-start gap-2 mt-1">
                <MapPin className="w-4 h-4 text-accent mt-1" />
                <span>Royal Thai Embassy Singapore<br />370 Orchard Road<br />Singapore 238870</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="pt-8 border-t border-accent/20 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-white/40 text-sm">© 2026 Thai Trade Center, Singapore. All rights reserved.</p>
        <div className="flex gap-6">
          <a href="#" className="text-white/40 hover:text-accent text-sm transition-colors">Privacy Policy</a>
          <a href="#" className="text-white/40 hover:text-accent text-sm transition-colors">Terms of Service</a>
          <a href="#" className="text-white/40 hover:text-accent text-sm transition-colors">Cookie Policy</a>
        </div>
      </div>
    </div>
  </footer>
);

// Brand Detail Modal
const BrandDetailModal = ({ brand, isOpen, onClose }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  
  if (!brand) return null;

  const openLightbox = (img) => {
    setLightboxImage(img);
    setLightboxOpen(true);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent hideCloseButton className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0A3D62] border-[#1a5276] p-0" data-testid="brand-detail-modal">
          <div className="relative">
            {/* Header with logo */}
            <div className="relative h-48 sm:h-72 bg-[#f5f5f5] flex items-center justify-center overflow-hidden">
              {brand.logo ? (
                <img src={brand.logo} alt={brand.name} className="h-full w-auto max-w-full object-contain" />
              ) : (
                <span className="text-6xl sm:text-8xl font-bold text-accent/30">{brand.name.charAt(0)}</span>
              )}
              {brand.is_featured && (
                <span className="absolute top-4 left-4 bg-[#0A3D62] text-white px-4 py-1 rounded text-xs font-bold">FEATURED</span>
              )}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0A3D62] flex items-center justify-center text-white hover:bg-[#072942] transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 sm:p-8">
              <span className="badge-category mb-4 inline-block">{brand.category}</span>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">{brand.name}</h2>
              
              {/* Gallery */}
              {brand.gallery && brand.gallery.length > 0 && (
                <div className="mb-8">
                  <h4 className="text-accent font-semibold mb-4">Gallery</h4>
                  <div className="grid grid-cols-3 sm:flex sm:flex-row gap-3 sm:overflow-x-auto pb-2">
                    {brand.gallery.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`${brand.name} gallery ${index + 1}`}
                        className="w-full sm:w-28 h-24 sm:h-28 rounded-lg object-cover flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity border-2 border-transparent hover:border-accent"
                        onClick={() => openLightbox(img)}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Investment Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 mb-8">
                {brand.franchise_fee && (
                  <div className="bg-[#072942] rounded-lg p-4 border-2 border-[#1a5276]">
                    <p className="text-white/40 text-xs uppercase mb-1">Franchise Fee</p>
                    <p className="text-accent font-semibold">{brand.franchise_fee}</p>
                  </div>
                )}
                {brand.royalty_fee && (
                  <div className="bg-[#072942] rounded-lg p-4 border-2 border-[#1a5276]">
                    <p className="text-white/40 text-xs uppercase mb-1">Royalty Fee</p>
                    <p className="text-accent font-semibold">{brand.royalty_fee}</p>
                  </div>
                )}
                {brand.total_investment && (
                  <div className="bg-[#072942] rounded-lg p-4 border-2 border-[#1a5276]">
                    <p className="text-white/40 text-xs uppercase mb-1">Initial Investment</p>
                    <p className="text-accent font-semibold">{brand.total_investment}</p>
                  </div>
                )}
                {brand.space_required && (
                  <div className="bg-[#072942] rounded-lg p-4 border-2 border-[#1a5276]">
                    <p className="text-white/40 text-xs uppercase mb-1">Space Required</p>
                    <p className="text-accent font-semibold">{brand.space_required}</p>
                  </div>
                )}
                {brand.established && (
                  <div className="bg-[#072942] rounded-lg p-4 border-2 border-[#1a5276]">
                    <p className="text-white/40 text-xs uppercase mb-1">Established</p>
                    <p className="text-accent font-semibold">{brand.established}</p>
                  </div>
                )}
              </div>
              
              {/* About */}
              {brand.about && (
                <div className="mb-8">
                  <h4 className="font-heading text-xl font-semibold text-white mb-3">About {brand.name}</h4>
                  <p className="text-white/70 leading-relaxed">{brand.about}</p>
                </div>
              )}
              
              {/* Highlights & Support */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {brand.key_highlights && brand.key_highlights.length > 0 && (
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-white mb-4">Key Highlights</h4>
                    <ul className="space-y-2">
                      {brand.key_highlights.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-white/70">
                          <Check className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {brand.franchise_support && brand.franchise_support.length > 0 && (
                  <div>
                    <h4 className="font-heading text-lg font-semibold text-white mb-4">Franchise Support</h4>
                    <ul className="space-y-2">
                      {brand.franchise_support.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-white/70">
                          <Check className="w-4 h-4 text-accent mt-1 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Ideal For */}
              {brand.ideal_for && brand.ideal_for.length > 0 && (
                <div className="mb-8">
                  <h4 className="font-heading text-lg font-semibold text-white mb-4">Ideal For</h4>
                  <div className="flex flex-wrap gap-2">
                    {brand.ideal_for.map((item, index) => (
                      <span key={index} className="px-4 py-2 rounded-full border-2 border-[#1a5276] text-white/70 text-sm">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Company Info */}
              <div className="bg-[#072942] rounded-xl p-6 mb-8 border-2 border-[#1a5276]">
                <h4 className="font-heading text-lg font-semibold text-white mb-4">Company Information</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  {brand.headquarters && (
                    <div>
                      <p className="text-white/40 text-xs uppercase mb-1">Headquarters</p>
                      <p className="text-white">{brand.headquarters}</p>
                    </div>
                  )}
                  {brand.global_outlets && (
                    <div>
                      <p className="text-white/40 text-xs uppercase mb-1">Total Outlets</p>
                      <p className="text-white">{brand.global_outlets}</p>
                    </div>
                  )}
                  {brand.website && (
                    <div>
                      <p className="text-white/40 text-xs uppercase mb-1">Website</p>
                      <a href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{brand.website}</a>
                    </div>
                  )}
                  {brand.show_contact_info !== false && brand.email && (
                    <div>
                      <p className="text-white/40 text-xs uppercase mb-1">Email</p>
                      <a href={`mailto:${brand.email}`} className="text-accent hover:underline">{brand.email}</a>
                    </div>
                  )}
                  {brand.show_contact_info !== false && brand.contact_person && (
                    <div>
                      <p className="text-white/40 text-xs uppercase mb-1">Contact Person</p>
                      <p className="text-white">{brand.contact_person}</p>
                    </div>
                  )}
                  {brand.show_contact_info !== false && brand.contact_number && (
                    <div>
                      <p className="text-white/40 text-xs uppercase mb-1">Contact Number</p>
                      <a href={`tel:${brand.contact_number}`} className="text-accent hover:underline">{brand.contact_number}</a>
                    </div>
                  )}
                </div>
                
                {/* Social Media */}
                {(brand.facebook_url || brand.instagram_url || brand.tiktok_url) && (
                  <div className="mt-6 pt-6 border-t border-[#1a5276]">
                    <p className="text-white/40 text-xs uppercase mb-3">Social Media</p>
                    <div className="flex gap-3">
                      {brand.facebook_url && (
                        <a href={brand.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                          <Facebook className="w-5 h-5" />
                        </a>
                      )}
                      {brand.instagram_url && (
                        <a href={brand.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {brand.tiktok_url && (
                        <a href={brand.tiktok_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                          <TikTokIcon className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* CTA */}
              <a
                href="#register"
                onClick={onClose}
                className="btn-gold-filled w-full rounded-lg flex items-center justify-center gap-2"
                data-testid="brand-express-interest-btn"
              >
                Express Interest <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent hideCloseButton className="max-w-5xl bg-black/95 border-none p-2" data-testid="lightbox-modal">
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors z-50"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Gallery full view"
            className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main HomePage Component
const HomePage = () => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`);
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'why-attend', 'brands', 'schedule', 'gallery'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBrandClick = (brand) => {
    setSelectedBrand(brand);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background relative">
      <FloatingDots />
      <div className="relative z-10">
        <Navigation activeSection={activeSection} />
        <HeroSection />
        <AboutSection />
        <WhyAttendSection />
        <FeaturedBrandsSection brands={brands} onBrandClick={handleBrandClick} />
        <ScheduleSection />
        <GalleryCarouselSection />
        <Footer />
      </div>
      <BrandDetailModal
        brand={selectedBrand}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default HomePage;
